/**
 * Advanced Rendering Module
 * 
 * Provides post-processing effects and advanced rendering features:
 * - Bloom
 * - SSAO (Screen-Space Ambient Occlusion)
 * - Outline for selected objects
 * - Anti-aliasing
 * - Tone mapping
 */

import { useRef, useMemo, useEffect } from 'react';
import { useThree } from '@react-three/fiber';
import {
    EffectComposer,
    Bloom,
    SSAO,
    Outline,
    ToneMapping,
    Vignette,
} from '@react-three/postprocessing';
import { BlendFunction, ToneMappingMode } from 'postprocessing';
import * as THREE from 'three';
import { defaultRenderSettings, RenderSettings } from './RenderingConstants';
import { useRenderSettingsStore } from '../state/useRenderSettingsStore';

/**
 * Post-Processing Effects Component
 */
export function PostProcessing({
    settings,
    selectedObjects = [],
}: {
    settings?: Partial<RenderSettings>;
    selectedObjects?: THREE.Object3D[];
}) {
    const storeSettings = useRenderSettingsStore((state) => state.settings);
    const config = useMemo(
        () => ({ ...defaultRenderSettings, ...(settings ?? storeSettings) }),
        [settings, storeSettings]
    );

    // Get tone mapping mode
    const toneMapping = useMemo(() => {
        switch (config.toneMappingMode) {
            case 'aces': return ToneMappingMode.ACES_FILMIC;
            case 'reinhard': return ToneMappingMode.REINHARD2;
            default: return ToneMappingMode.LINEAR;
        }
    }, [config.toneMappingMode]);

    // Parsed once per config instead of a fresh THREE.Color per render.
    const outlineHex = useMemo(() => new THREE.Color(config.outlineColor).getHex(), [config.outlineColor]);

    // Composer gating: when nothing besides tone mapping is active AND the
    // mode matches the renderer-level default (ACES, see ENGINE_GL_CONFIG),
    // the whole post chain is skipped — the renderer's own ACES + native MSAA
    // produce the same image without the per-frame 8× MSAA target + copy pass.
    // Any non-default tone mode or enabled effect keeps the composer mounted.
    // Exposure still applies: AdvancedLighting writes gl.toneMappingExposure.
    const anyEffectActive =
        config.bloomEnabled
        || config.ssaoEnabled
        || config.vignetteEnabled
        || (config.outlineEnabled && selectedObjects.length > 0);
    const toneNeedsComposer = config.toneMappingMode !== 'aces';
    if (!anyEffectActive && !toneNeedsComposer) {
        return null;
    }

    return (
        <EffectComposer multisampling={config.antialias ? 8 : 0}>
            {/* Tone Mapping */}
            <ToneMapping mode={toneMapping} />

            {/* Bloom Effect */}
            {config.bloomEnabled ? (
                <Bloom
                    intensity={config.bloomIntensity}
                    luminanceThreshold={config.bloomThreshold}
                    luminanceSmoothing={0.9}
                    blendFunction={BlendFunction.ADD}
                />
            ) : <></>}

            {/* SSAO Effect */}
            {config.ssaoEnabled ? (
                <SSAO
                    radius={config.ssaoRadius}
                    intensity={config.ssaoIntensity}
                    luminanceInfluence={0.6}
                    blendFunction={BlendFunction.MULTIPLY}
                    worldDistanceThreshold={1.0}
                    worldDistanceFalloff={0.1}
                    worldProximityThreshold={1.0}
                    worldProximityFalloff={0.1}
                />
            ) : <></>}

            {/* Selection Outline */}
            {config.outlineEnabled && selectedObjects.length > 0 ? (
                <Outline
                    selection={selectedObjects}
                    visibleEdgeColor={outlineHex}
                    hiddenEdgeColor={0x22090a}
                    edgeStrength={3}
                    pulseSpeed={0.0}
                    blur
                />
            ) : <></>}

            {/* Vignette */}
            {config.vignetteEnabled ? (
                <Vignette
                    offset={0.3}
                    darkness={config.vignetteIntensity}
                    blendFunction={BlendFunction.NORMAL}
                />
            ) : <></>}
        </EffectComposer>
    );
}

/**
 * Enhanced Lighting Setup Component
 */
export function AdvancedLighting({
    settings,
}: {
    settings?: Partial<RenderSettings>;
}) {
    const storeSettings = useRenderSettingsStore((state) => state.settings);
    const config = useMemo(
        () => ({ ...defaultRenderSettings, ...(settings ?? storeSettings) }),
        [settings, storeSettings]
    );
    const directionalRef = useRef<THREE.DirectionalLight>(null);

    // Get shadow map type
    const shadowType = useMemo(() => {
        switch (config.shadowType) {
            case 'basic': return THREE.BasicShadowMap;
            case 'pcf': return THREE.PCFShadowMap;
            case 'vsm': return THREE.VSMShadowMap;
            default: return THREE.PCFSoftShadowMap;
        }
    }, [config.shadowType]);

    // Update renderer shadow map type + exposure (side effects moved to effect)
    const { gl, scene, invalidate } = useThree();
    useEffect(() => {
        gl.shadowMap.type = shadowType;
        gl.shadowMap.enabled = config.shadowsEnabled;
        gl.shadowMap.needsUpdate = true;
        gl.toneMappingExposure = config.exposure;
        // Demand render loop: re-render after renderer state changes
        invalidate();
    }, [gl, shadowType, config.shadowsEnabled, config.exposure, invalidate]);

    // Configure the shadow frustum dynamically from the current scene bounds
    // (F8 fix: no more hardcoded far=50 / d=20, works for large scenes)
    useEffect(() => {
        const light = directionalRef.current;
        if (!light || !light.shadow || !light.shadow.camera) return;

        const shadowCamera = light.shadow.camera as THREE.OrthographicCamera;
        light.shadow.mapSize.width = config.shadowMapSize;
        light.shadow.mapSize.height = config.shadowMapSize;
        light.shadow.bias = -0.0001;

        const bounds = new THREE.Box3().setFromObject(scene);
        const extent = bounds.isEmpty()
            ? 20
            : Math.max(
                20,
                Math.ceil(Math.max(
                    bounds.max.x - bounds.min.x,
                    bounds.max.y - bounds.min.y,
                    bounds.max.z - bounds.min.z
                ) * 0.75)
            );

        shadowCamera.left = -extent;
        shadowCamera.right = extent;
        shadowCamera.top = extent;
        shadowCamera.bottom = -extent;
        shadowCamera.near = 0.1;
        shadowCamera.far = bounds.isEmpty() ? 50 : Math.max(50, extent * 4);
        shadowCamera.updateProjectionMatrix();
        light.shadow.needsUpdate = true;
        invalidate();
    }, [config.shadowMapSize, config.shadowsEnabled, scene, invalidate]);

    return (
        <>
            {/* Ambient Light */}
            <ambientLight intensity={0.4} />

            {/* Main Directional Light (Sun) */}
            <directionalLight
                ref={directionalRef}
                position={[10, 20, 10]}
                intensity={1.2}
                castShadow={config.shadowsEnabled}
            />

            {/* Fill Light */}
            <directionalLight
                position={[-10, 10, -10]}
                intensity={0.3}
            />

            {/* Hemisphere Light (Sky/Ground) */}
            <hemisphereLight args={['#87ceeb', '#8b4513', 0.3]} />
        </>
    );
}
